import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({
      ...prev,
      service: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission (mock functionality for prototype)
    setTimeout(() => {
      // Store in localStorage for demo purposes
      const existingInquiries = JSON.parse(localStorage.getItem('inquiries') || '[]');
      const newInquiry = {
        ...formData,
        id: Date.now(),
        date: new Date().toISOString(),
        status: 'pending'
      };
      existingInquiries.push(newInquiry);
      localStorage.setItem('inquiries', JSON.stringify(existingInquiries));

      // Show success message
      toast.success('Message Sent Successfully!', {
        description: 'We\'ll get back to you within 24 hours.',
      });

      // Reset form
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  const contactDetails = [
    {
      icon: Phone,
      title: 'Phone',
      value: '07356 255155',
      link: 'tel:07356255155',
      description: 'Monday to Friday, 8am-5pm'
    },
    {
      icon: Mail,
      title: 'Email',
      value: 'eliteplumbersltd@gmail.com',
      link: 'mailto:eliteplumbersltd@gmail.com',
      description: 'We reply within 24 hours'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'London, United Kingdom',
      link: null,
      description: 'Serving all London boroughs'
    },
    {
      icon: Clock,
      title: 'Hours',
      value: 'Mon-Fri: 8am-5pm',
      link: null,
      description: 'Saturday by appointment'
    }
  ];

  const services = [
    'Professional Plumbing',
    'Leak Detection & Repairs',
    'Bathroom Installation',
    'Kitchen Installation',
    'Boiler & Heating Services',
    'Pipework Installation',
    'Water Pressure Issues',
    'General Plumbing',
    'Other'
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-b from-muted/30 to-background">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-6 mb-16 animate-slide-up">
            <Badge className="bg-primary/10 text-primary border-primary/20">
              Get in Touch
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
              Contact{' '}
              <span className="text-primary">Elite Plumbers</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Need professional plumbing services? Get in touch with us today for a quote. 
              With over 20 years of experience and a decade working with London Boroughs, 
              we\'re here to help make your property safe and suitable.
            </p>
          </div>

          {/* Contact Details Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {contactDetails.map((detail, index) => {
              const Icon = detail.icon;
              return (
                <Card 
                  key={index}
                  className="p-6 text-center border-2 hover:border-primary/50 hover:shadow-lg transition-all duration-300 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary/20 group-hover:scale-110 transition-all duration-300">
                    <Icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">
                    {detail.title}
                  </h3>
                  {detail.link ? (
                    <a 
                      href={detail.link}
                      className="text-sm text-primary hover:underline font-medium block mb-1"
                    >
                      {detail.value}
                    </a>
                  ) : (
                    <p className="text-sm text-foreground font-medium mb-1">{detail.value}</p>
                  )}
                  <p className="text-xs text-muted-foreground">{detail.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 md:py-32">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
                  Send Us a Message
                </h2>
                <p className="text-muted-foreground">
                  Fill out the form below and we\'ll get back to you as soon as possible.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium">
                    Full Name *
                  </Label>
                  <Input
                    id="fullName"
                    name="fullName"
                    type="text"
                    placeholder="John Smith"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="border-2 focus:border-primary transition-colors duration-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="border-2 focus:border-primary transition-colors duration-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium">
                    Phone Number *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="+44 20 0000 0000"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="border-2 focus:border-primary transition-colors duration-300"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="text-sm font-medium">
                    Service Required *
                  </Label>
                  <Select onValueChange={handleServiceChange} value={formData.service} required>
                    <SelectTrigger className="border-2 focus:border-primary transition-colors duration-300">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service} value={service}>
                          {service}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-sm font-medium">
                    Message *
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Tell us about your plumbing needs..."
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="border-2 focus:border-primary transition-colors duration-300 resize-none"
                  />
                </div>

                <Button 
                  type="submit"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow transition-all duration-300 group"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mr-2" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <Send className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  By submitting this form, you agree to our Privacy Policy and Terms of Service.
                </p>
              </form>
            </div>

            {/* Info Section */}
            <div className="space-y-8">
              {/* Why Choose Us */}
              <Card className="p-8 border-2">
                <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
                  Why Choose Elite Plumbers?
                </h3>
                <div className="space-y-4">
                  {[
                    'Over 20 years of professional plumbing experience',
                    'Certified and Gas Safe registered engineers',
                    'Trusted partners with London Boroughs for 10+ years',
                    'Transparent, upfront pricing with no hidden fees',
                    'Fully insured and all work guaranteed',
                    'Making London homes safe and suitable for everyone'
                  ].map((point, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-muted-foreground">{point}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Contact Card */}
              <Card className="p-8 bg-secondary text-secondary-foreground border-2">
                <h3 className="text-xl font-heading font-bold mb-4">
                  Get Professional Advice
                </h3>
                <p className="text-sm text-secondary-foreground/80 mb-6">
                  Speak to our experienced team about your plumbing needs. You can call us 
                  Monday to Friday, 8am-5pm, or send us an email anytime and we'll reply within 24 hours.
                </p>
                <div className="space-y-3">
                  <Button 
                    asChild
                    size="lg"
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg hover:shadow-glow"
                  >
                    <a href="tel:07356255155" className="flex items-center justify-center gap-2">
                      <Phone className="w-5 h-5" />
                      Call 07356 255155
                    </a>
                  </Button>
                  <Button 
                    asChild
                    size="lg"
                    variant="outline"
                    className="w-full bg-transparent border-2 border-secondary-foreground/20 hover:bg-secondary-foreground/10"
                  >
                    <a href="mailto:eliteplumbersltd@gmail.com" className="flex items-center justify-center gap-2">
                      <Mail className="w-5 h-5" />
                      Send Email
                    </a>
                  </Button>
                </div>
              </Card>

              {/* Service Areas */}
              <Card className="p-8 border-2">
                <h3 className="text-xl font-heading font-bold text-foreground mb-4">
                  Service Areas
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  We proudly serve all major London boroughs including:
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Lambeth', 'Wandsworth', 'Westminster', 'City of London', 'Fulham', 'Brent', 'Bromley', 'Camden'].map((area) => (
                    <Badge key={area} variant="outline" className="border-primary/30 text-primary">
                      {area}
                    </Badge>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}